import { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import { ChatBubble } from './ChatBubble';
import { ImageGenerationCard } from './ImageGenerationCard';

function initialChatWindowSize() {
  const width = Math.max(260, Math.min(380, window.innerWidth - 20));
  const height = Math.max(320, Math.min(500, window.innerHeight - 96));
  return { w: width, h: height };
}

export function ChatWindow({ messages, onClose, sessionId }) {
  const [size, setSize] = useState(() => initialChatWindowSize());
  const [pos, setPos] = useState(() => {
    const initial = initialChatWindowSize();
    return { x: Math.max(10, window.innerWidth - initial.w - 24), y: 80 };
  });
  const messagesEndRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { ox: e.clientX - pos.x, oy: e.clientY - pos.y };
    const move = (e) => {
      const maxX = Math.max(10, window.innerWidth - size.w - 10);
      const maxY = Math.max(10, window.innerHeight - size.h - 10);
      setPos({
        x: Math.min(maxX, Math.max(10, e.clientX - dragRef.current.ox)),
        y: Math.min(maxY, Math.max(10, e.clientY - dragRef.current.oy)),
      });
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { sx: e.clientX, sy: e.clientY, sw: size.w, sh: size.h };
    const move = (e) => setSize({
      w: Math.min(Math.max(260, window.innerWidth - pos.x - 10), Math.max(260, resizeRef.current.sw + e.clientX - resizeRef.current.sx)),
      h: Math.min(Math.max(320, window.innerHeight - pos.y - 10), Math.max(320, resizeRef.current.sh + e.clientY - resizeRef.current.sy)),
    });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.93, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.93, y: 10 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
      className="fixed z-50 flex flex-col rounded-2xl overflow-hidden border border-blue-500/10 bg-[#080812]/92 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.65)]"
    >
      {/* Title bar — drag handle */}
      <div
        onPointerDown={startDrag}
        className="flex items-center justify-between px-4 py-2.5 border-b border-blue-500/8 cursor-grab active:cursor-grabbing select-none shrink-0 bg-[#0a0a1a]/50"
      >
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400/50" />
          <span className="text-[10px] font-mono text-white/25 tracking-widest uppercase">Conversa</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-md hover:bg-white/5 transition-colors group">
          <X size={13} className="text-white/20 group-hover:text-white/55 transition-colors" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 overscroll-contain">
        {messages.length === 0 ? (
          <p className="text-center text-white/12 text-xs font-mono mt-10 tracking-wider">nenhuma mensagem ainda</p>
        ) : (
          messages.slice(-50).map((msg, i) => (
            msg.type === 'visual_aid' ? (
              <div key={msg.response_message_id || msg.id || msg.timestamp || `image-${i}`} className="mb-3">
                <ImageGenerationCard
                  imageUrl={msg.imageUrl}
                  prompt={msg.prompt}
                  provider={msg.provider}
                  isLoading={Boolean(msg.isLoading)}
                  isError={Boolean(msg.error)}
                />
              </div>
            ) : (
              <ChatBubble
                key={msg.response_message_id || msg.id || msg.timestamp || `${msg.role}-${i}`}
                message={msg.content}
                isAthena={msg.role === 'assistant'}
                presentation={msg.response_presentation}
                sessionId={sessionId || msg.session_id}
                messageId={msg.response_message_id || msg.card_id || msg.id || msg.timestamp || `${msg.role}-${i}`}
                timestamp={msg.timestamp}
              />
            )
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Resize handle — bottom-right corner */}
      <div
        onPointerDown={startResize}
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize opacity-30 hover:opacity-60 transition-opacity"
        style={{ background: 'linear-gradient(135deg, transparent 55%, rgba(59,130,246,0.4) 55%)' }}
      />
    </motion.div>
  );
}
